import React, { FC } from 'react'
import DataTable, { TableColumn } from '../../../../shared/components/DataTable'
import { IAuthActivityLogModel } from './AuthActivityLogModel'

interface AuthActivityLogTableProps {
  data: IAuthActivityLogModel[]
  loading: boolean
  currentPage: number
  totalPages: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onSort: (column: string, order: 'asc' | 'desc') => void
}

const formatDateTime = (isoString: string): string => {
  if (!isoString) return '-'
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return isoString
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const ActionBadge: FC<{ action: string }> = ({ action }) => {
  const isLogin = action === 'LOGIN'
  const isLogout = action === 'LOGOUT'
  const cls = isLogin
    ? 'badge-light-success'
    : isLogout
      ? 'badge-light-info'
      : 'badge-light-secondary'
  const icon = isLogin ? 'bi-box-arrow-in-right' : isLogout ? 'bi-box-arrow-right' : 'bi-activity'
  return (
    <span className={`badge ${cls} fw-bold`}>
      <i className={`bi ${icon} me-1`} />
      {action}
    </span>
  )
}

const SuccessBadge: FC<{ success: boolean }> = ({ success }) => (
  <span className={`badge ${success ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
    <i className={`bi ${success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`} />
    {success ? 'Başarılı' : 'Başarısız'}
  </span>
)

const UserCell: FC<{ username: string; userId: string | null }> = ({ username, userId }) => {
  const initial = username?.[0]?.toUpperCase() ?? '?'
  const isUnknown = !userId || username === 'unknown'
  const symbolClass = isUnknown ? 'bg-light-warning text-warning' : 'bg-light-primary text-primary'
  return (
    <div className='d-flex align-items-center gap-3'>
      <div className='symbol symbol-35px'>
        <span className={`symbol-label ${symbolClass} fw-bold fs-6`}>{initial}</span>
      </div>
      <div className='d-flex flex-column'>
        <span className='fw-bold text-dark'>{username || '-'}</span>
        {userId ? (
          <span className='text-muted fs-8' title={userId}>
            {userId.length > 12 ? `${userId.slice(0, 8)}…${userId.slice(-4)}` : userId}
          </span>
        ) : (
          <span className='text-muted fs-8 fst-italic'>Kullanıcı yok</span>
        )}
      </div>
    </div>
  )
}

const AuthActivityLogTable: FC<AuthActivityLogTableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortBy,
  sortOrder,
  onSort,
}) => {
  const columns: TableColumn<IAuthActivityLogModel>[] = [
    {
      key: 'id',
      label: '#',
      sortable: true,
      width: '70px',
      render: (value) => <span className='text-muted fw-semibold'>{value}</span>,
    },
    {
      key: 'username',
      label: 'Kullanıcı',
      sortable: true,
      render: (_v, row) => <UserCell username={row.username} userId={row.userId} />,
    },
    {
      key: 'action',
      label: 'İşlem',
      sortable: true,
      width: '140px',
      render: (value) => <ActionBadge action={value as string} />,
    },
    {
      key: 'success',
      label: 'Sonuç',
      sortable: true,
      width: '140px',
      render: (value) => <SuccessBadge success={Boolean(value)} />,
    },
    {
      key: 'message',
      label: 'Mesaj',
      render: (value) => (
        <span className='text-gray-700' title={value as string}>
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'loggedAt',
      label: 'Tarih',
      sortable: true,
      width: '200px',
      render: (value) => (
        <span className='text-gray-700 fw-semibold'>
          <i className='bi bi-clock-history me-2 text-muted' />
          {formatDateTime(value as string)}
        </span>
      ),
    },
  ]

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      hover
      emptyMessage='Henüz kayıt bulunamadı'
    />
  )
}

export default AuthActivityLogTable
